import React, { useState } from 'react';
import { Link, Message } from '@flos/react-ui';
import { useRecoilValue } from 'recoil';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import { useHippoText } from '../../hooks/useHippoText';
import ViewInformationModal from '../ViewInformationModal/ViewInformationModal';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { giftCertificateAtom } from '../../recoil/atoms/giftCertificateAtom';
import { COVERABLE_CODE } from '../../constants/coverable';
import { isGiftCardEligibleSelector } from '../../recoil/selectors/isGiftCardEligibleSelector';

interface SelectedGiftCard {
    value: number;
    flavourName: string;
    flavourCode: string;
}

const GiftCertificateShoppingBasket = (): React.JSX.Element => {
    const giftCoverableMap = useRecoilValue(giftCertificateAtom);
    const isGiftCardEligible = useRecoilValue(isGiftCardEligibleSelector);
    const getHippoText = useHippoText();
    const [giftCardSelected, setGiftCardSelected] = useState<SelectedGiftCard>({
        value: 0,
        flavourCode: '',
        flavourName: ''
    });
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);

    const { value, flavourCode, flavourName } = giftCardSelected;

    const modalText =
        flavourCode === COVERABLE_CODE.accident
            ? 'DSS-GIFT-MODAL-DESCRIPTION-ACCIDENT'
            : 'DSS-GIFT-MODAL-DESCRIPTION';

    const giftEnabledCoverables = sortedCoverables.reduce<Record<string, CoverableDTO[]>>(
        (acc, current) => {
            const { flavourCode, isAddedInOnsa, status } = current;
            if (!isAddedInOnsa || status !== 'Added') return acc;
            const value = giftCoverableMap[flavourCode];
            if (!value) return acc;
            if (!acc[flavourCode] || acc[flavourCode].length < 5) {
                acc[flavourCode] = (acc[flavourCode] || []).concat(current);
            }
            return acc;
        },
        {}
    );

    if (!isGiftCardEligible) {
        return <></>;
    }

    return (
        <div>
            {Object.values(giftEnabledCoverables)
                .flat()
                .map(({ flavourCode, fixedId, flavourName, willSpinOff, name = '' }) => {
                    const giftCertificateValue = giftCoverableMap[flavourCode];
                    const giftTo =
                        flavourCode === COVERABLE_CODE.accident && willSpinOff
                            ? ` til ${name}`
                            : '';
                    return (
                        <Message
                            key={flavourCode + fixedId}
                            theme="success"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Link
                                onClick={() => {
                                    setGiftCardSelected({
                                        value: giftCertificateValue,
                                        flavourCode,
                                        flavourName
                                    });
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <h6 style={{ margin: 0 }}>
                                        {getHippoText('DSS-SHOPPING-BASKET-GIFT-CARD-LABEL')}
                                        {giftTo}
                                        {': '}
                                        {flavourName}
                                    </h6>
                                </div>
                            </Link>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <h6 style={{ margin: 0 }}>
                                    {giftCertificateValue}{' '}
                                    {getHippoText('DSS-SHOPPING-BASKET-GIFT-CARD-VALUE-TEXT')}
                                </h6>
                            </div>
                        </Message>
                    );
                })}
            <ViewInformationModal
                showModal={value !== 0}
                modalProps={{
                    title: getHippoText('DSS-GIFT-MODAL-TITLE'),
                    description: getHippoText(modalText, {
                        value,
                        flavour: flavourName.toLowerCase()
                    }),
                    secondDescription: getHippoText('DSS-GIFT-MODAL-DESCRIPTION-TWO')
                }}
                closeModal={() => {
                    setGiftCardSelected({
                        value: 0,
                        flavourCode: '',
                        flavourName: ''
                    });
                }}
            />
        </div>
    );
};

export default GiftCertificateShoppingBasket;
